// File: Table.tsx
import { useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import styles from "./styles/Table.module.css"
import { HighlightContext, TimePreferencesContext } from './App';
import globalStyles from "./global.module.css"
import studentOverlayStyles from './styles/StudentOverlay.module.css'
import { completeTimesList, completeDaysList } from "./App";
import TableCell from "./TableCell";
import ConfirmModal from './components/ConfirmModal';
import { useResponsiveContext } from "./contexts/ResponsiveContext";
import { useSchedulesCache } from "./contexts/SchedulesCacheContext";
import type { StudentSchedulesEntry } from "./contexts/SchedulesCacheContext";
import ScheduleTooltip from "./components/ScheduleTooltip";
import { debounce } from "./utils/debounce";
import { isSlotAvailable, dayTimeToSlot } from "./utils/timeSlots";
import type { StudentDragItem, StudentOverlayData } from "./StudentOverlay";
import type { PanelStudent } from "./StudentsInfoPanel/StudentPanel";
import type { DisplaySchedule } from "./utils/scheduleHelpers";

const DEBUG_HOVER = process.env.NODE_ENV === "development" && false;
const debugHover = (...args: unknown[]) => {
  if (DEBUG_HOVER) {
    console.log(...args);
  }
};

type StudentScheduleRow = StudentSchedulesEntry['schedules'][number];

/** Overlay-Datensatz plus die Zell-ID, in der er startet. */
type TableOverlay = StudentOverlayData & { timeSlotId: string };

interface PendingDrop {
  name: string;
  durationSlots: number;
  day: string | number;
  time: string;
  memberId: string;
  userId?: string | null;
}

interface TableProps {
  timeIndexOffset?: number;
  timesList: readonly string[];
  timeIndexRange?: { start: number; end: number };
  schedule?: DisplaySchedule | null;
  studentsList?: PanelStudent[] | null;
  onScheduleUpdate?: (schedule: DisplaySchedule, updatedEntry: unknown, members?: unknown, newStudentsList?: unknown) => void;
  onLockToggle?: (memberId: string) => void;
  classMembers?: unknown[] | null;
  classId?: string | null;
  selectedClass?: unknown;
}

export default function Table({
  timeIndexOffset, timesList, timeIndexRange, schedule, studentsList, onScheduleUpdate, onLockToggle, classMembers, classId, selectedClass }: TableProps) {


    const { isMobile } = useResponsiveContext();

    const tableRef = useRef<HTMLDivElement>(null);

    const elementBelowCursorRef = useRef<Element | null>(null);

    const [highlightedStudent] = useState<string | null>(null);
    
    // *** Nutze den SchedulesCache statt lokalem State ***
    const { getSchedulesForStudent, loadSchedulesForStudent } = useSchedulesCache();
    
    // State für Tooltip
    const [tooltipState, setTooltipState] = useState<{
      visible: boolean;
      position: { x: number; y: number } | null;
      schedules: StudentScheduleRow[];
      studentMemberIds: string[];
      classesWithPrimary: string[];
    }>({
      visible: false,
      position: null,
      schedules: [],
      studentMemberIds: [],
      classesWithPrimary: []
    });



    // containing only the ids of TimeSlots that Students have as start times
    const [, setStudentStartTimeTimeSlotIds] = useState<string[]>([])
    // containing all necessary information to render a StudentOverlay
    const [studentOverlays, setStudentOverlays] = useState<TableOverlay[]>([]);

    // F1: member_id -> student Map, um wiederholte O(n)-Lookups (studentsList.find) zu sparen.
    const studentsByMemberId = useMemo(
      () => new Map((studentsList || []).map(s => [s.member_id, s] as const)),
      [studentsList]
    );
    // F2: "day_startTime" -> Overlays Map, statt pro Zelle über alle Overlays zu filtern.
    const overlaysByCell = useMemo(() => {
      const map = new Map<string, TableOverlay[]>();
      for (const overlay of studentOverlays) {
        const key = `${overlay.day}_${overlay.startTime}`;
        const list = map.get(key);
        if (list) list.push(overlay); else map.set(key, [overlay]);
      }
      return map;
    }, [studentOverlays]);
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        studentName: string;
        pendingDrop: PendingDrop | null;
    }>({
        isOpen: false,
        studentName: '',
        pendingDrop: null
    });

    // Beim Drag & Drop keine unnötige Neuberechnung durchführen
    const previousStudentsListRef = useRef<string | null>(null);

    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null); // Ref for the hover timer
    const toggleHighlightCells = useContext(HighlightContext); // Get context function
    const { timePreferences } = useContext(TimePreferencesContext); // Get time preferences


    // Debug-Informationen
    useEffect(() => {
      console.log("[Table.js] component received props:");
      console.log("  timesList:", timesList);
      console.log("  schedule:", schedule);
      console.log("  studentsList:", studentsList);
      console.log("  timeIndexRange:", timeIndexRange);
    }, [timesList, schedule, studentsList, timeIndexRange]);

    // Define isAvailableInternal before the overlay effect so it can be used inside
    const isAvailableInternal = useCallback((memberId: string, day: number | string, time: string): boolean => {

      // F1: ein einziger Map-Lookup statt zweier studentsList.find-Scans.
      const student = studentsByMemberId.get(memberId);
      if (!student) {
        return false;
      }

      // Use timePreferences to determine which times to use
      let timesToUse: any;
      const preference = timePreferences?.[memberId];

      // Use the preference if set
      if (preference === 'available_times' && student.available_times) {
        timesToUse = student.available_times;
      } else if (preference === 'available_times_user_input' && student.available_times_user_input) {
        timesToUse = student.available_times_user_input;
      } 
      // Fallback to old logic if preference not set
      else if (student.available_times) {
        timesToUse = student.available_times;
      } else if (student.available_times_user_input) {
        timesToUse = student.available_times_user_input;
      } else if (student.free_times) {
        timesToUse = student.free_times;
      }
      
      const availableTimes = timesToUse;

      if (!availableTimes) {
        return false;
      }

      return isSlotAvailable(availableTimes as never, day as number, time);
    }, [studentsByMemberId, timePreferences]);

    // Memoization für studentOverlays, um unnötige Neuberechnungen zu vermeiden
    useEffect(() => {
      console.log("[Table.js Overlay Effect] Running. Received studentsList:", studentsList);

      if (!studentsList || studentsList.length === 0 || !completeTimesList) {
        console.log("[Table.js Overlay Effect] Early return condition met:", { hasStudentsList: !!studentsList, keysLength: studentsList ? studentsList.length : 0, hasCompleteTimesList: !!completeTimesList });
        setStudentOverlays([]);
        setStudentStartTimeTimeSlotIds([]);
        previousStudentsListRef.current = null;
        return;
      }

      // Include timePreferences in memoization check
      const currentDataString = JSON.stringify({ studentsList, timePreferences });
      if (
        previousStudentsListRef.current &&
        previousStudentsListRef.current === currentDataString
      ) {
        return;
      }

      console.log("[Table.js Overlay Effect] studentsList or timePreferences has changed or is initial run, proceeding to calculate overlays...");
      previousStudentsListRef.current = currentDataString;

      const newTimeSlotIdsForOverlays: string[] = [];
      const newOverlays: TableOverlay[] = [];
      let foundSetStudent = false;

      studentsList.forEach((studentInfo) => {
        if (!studentInfo) {
          console.warn(`[Table.js Overlay Effect] Keine studentInfo gefunden`);
          return;
        }
        const day = studentInfo.set_day;
        // console.log(studentInfo)

        

        if (day === null || day === undefined || !studentInfo.set_time_from_to || studentInfo.set_time_from_to.length < 2 || !studentInfo.is_set) {
          // console.log(`[Table.js Overlay Effect] Skipping ${studentInfo.email || studentInfo.name} because not set (day: ${day}, is_set: ${studentInfo.is_set}).`);
          return;
        }

        foundSetStudent = true;
        const startTimeId = studentInfo.set_time_from_to![0];
        // const endTimeId = studentInfo.set_time_from_to[1]; // Nicht mehr primär verwenden
        // let durationSlots = endTimeId - startTimeId;       // Alte Berechnung

        // === BEGIN EDIT ===
        // Berechne durationSlots basierend auf der Dauer in Minuten (robuster)
        const durationMinutes = studentInfo.duration; // z.B. 30
        let durationSlots = 1; // Fallback-Wert

        if (durationMinutes && Number(durationMinutes) > 0) {
            durationSlots = Math.round(Number(durationMinutes) / 15); // z.B. 30 / 15 = 2 Slots
            // Optional: Überprüfen, ob die Dauer aus set_time_from_to übereinstimmt (nur zum Debuggen)
            if (studentInfo.set_time_from_to!.length === 2) {
                const endTimeIdFromSetTime = studentInfo.set_time_from_to![1];
                const durationFromSetTime = endTimeIdFromSetTime - startTimeId;
                if (durationSlots !== durationFromSetTime && durationFromSetTime > 0) {
                     console.warn(`[Table.js Overlay Effect] Inkonsistente Dauer für ${studentInfo.name} (${studentInfo.member_id}): ${durationSlots} (aus duration) vs ${durationFromSetTime} (aus set_time_from_to). Verwende ${durationSlots}.`);
                     // Wenn die Dauer aus set_time_from_to nur um 1 abweicht, könnte es das Inklusiv/Exklusiv-Problem sein.
                     if (Math.abs(durationSlots - durationFromSetTime) === 1) {
                        console.warn(`   -> Mögliches Inklusiv/Exklusiv-Problem in set_time_from_to für ${studentInfo.name}.`);
                     }
                } else if (durationFromSetTime <= 0) {
                    console.warn(`[Table.js Overlay Effect] Ungültige Dauer aus set_time_from_to (${durationFromSetTime}) für ${studentInfo.name} (${studentInfo.member_id}). Verwende ${durationSlots} aus duration.`);
                }
            }
        } else {
            // Fallback, wenn duration fehlt oder ungültig ist: Versuche es aus set_time_from_to
            if (studentInfo.set_time_from_to!.length === 2) {
                const endTimeIdFromSetTime = studentInfo.set_time_from_to![1];
                const calculatedDuration = endTimeIdFromSetTime - startTimeId;
                if (calculatedDuration > 0) {
                    durationSlots = calculatedDuration;
                    console.warn(`[Table.js Overlay Effect] Fehlende oder ungültige Dauer für ${studentInfo.name} (${studentInfo.member_id}). Verwende Dauer aus set_time_from_to: ${durationSlots} Slots.`);
                } else {
                     console.warn(`[Table.js Overlay Effect] Ungültige Dauer (${calculatedDuration}) für ${studentInfo.name} (${studentInfo.member_id}) auch aus set_time_from_to. Setze Fallback auf 1.`);
                     durationSlots = 1; // Sicherer Fallback
                }
           } else {
                console.warn(`[Table.js Overlay Effect] Fehlende Dauer und ungültiges set_time_from_to für ${studentInfo.name} (${studentInfo.member_id}). Setze Fallback auf 1.`);
                durationSlots = 1; // Sicherer Fallback
           }
        }
        // === END EDIT ===


        // console.log(`startTimeId: ${startTimeId}, durationSlots: ${durationSlots} for ${studentInfo.name} (${studentInfo.member_id})`) // Angepasstes Logging

        if (startTimeId < 0 || startTimeId >= completeTimesList.length) {
          console.warn(`[Table.js Overlay Effect] Ungültige startTimeId ${startTimeId} für ${studentInfo.name} (${studentInfo.member_id})`);
          return;
        }
        const startTime = completeTimesList[startTimeId];

        if (durationSlots <= 0) {
          // Diese Warnung sollte jetzt seltener auftreten, da wir primär studentInfo.duration nutzen
          console.warn(`[Table.js Overlay Effect] Ungültige oder berechnete Dauer (${durationSlots} Slots) für ${studentInfo.name} (${studentInfo.member_id}). Überprüfe studentInfo.duration.`);
          // Kein Fallback mehr nötig, da oben bereits behandelt
          return;
        }

        const studentId = studentInfo.user_id;
        const memberId = studentInfo.member_id;

        const timeSlotIdString = `${day}_${startTime}_timeSlot`;

        let isStudentAvailable = true;
        for(let i = 0; i < durationSlots; i++) {
          const currentTimeIndex = startTimeId + i;
          if (currentTimeIndex < completeTimesList.length) {
            const currentTime = completeTimesList[currentTimeIndex];
            if (!isAvailableInternal(memberId, day, currentTime)) {
              isStudentAvailable = false;
              break;
            }
          } else {
            isStudentAvailable = false;
            break;
          }
        }

        if (!newTimeSlotIdsForOverlays.includes(timeSlotIdString)) {
          newTimeSlotIdsForOverlays.push(timeSlotIdString);
        }

        // Compute isFavorite: all durationSlots consecutive slots from startTime must be in favorite_times
        const favMap = studentInfo.favorite_times as Record<string, string[]> | null | undefined;
        let isFavorite = false;
        if (favMap) {
          const dayFavs = favMap[String(day)] || [];
          if (dayFavs.length > 0) {
            isFavorite = true;
            for (let i = 0; i < durationSlots; i++) {
              const slotIdx = startTimeId + i;
              if (slotIdx >= completeTimesList.length) { isFavorite = false; break; }
              const slotTime = completeTimesList[slotIdx];
              if (!isSlotAvailable(favMap as never, day as number, slotTime)) {
                isFavorite = false;
                break;
              }
            }
          }
        }

        newOverlays.push({
          studentId: studentId,
          memberId: memberId,
          timeSlotId: timeSlotIdString,
          studentName: studentInfo.name as string,
          split_label: studentInfo.split_label,
          startTimeId,
          startTime,
          day,
          durationMult: durationSlots, // durationMult ist jetzt basierend auf studentInfo.duration oder Fallback
          isAvailable: isStudentAvailable,
          isNotAssigned: studentInfo.is_not_assigned ?? false,
          isLocked: studentInfo.is_locked ?? false,
          isFavorite,
        });
      });

      console.log(`[Table.js Overlay Effect] Finished calculation. Found any set student: ${foundSetStudent}. Calculated newOverlays:`, newOverlays);

      setStudentOverlays(newOverlays);
      console.log("newOverlays")
      console.log(newOverlays)
      setStudentStartTimeTimeSlotIds(newOverlays.map(o => o.timeSlotId));
    }, [studentsList, timePreferences, isAvailableInternal]); // eslint-disable-line react-hooks/exhaustive-deps

    // Hilfsfunktion: Konvertiere Backend-Format zu Frontend-Format
    // Extrahiert nur Zeiten, in denen die member_ids des Studenten vorkommen
    // ONLY considers primary schedules - if no primary exists, no conflicts are shown
    const convertScheduleToConflictTimes = useCallback((
      schedules: StudentScheduleRow[],
      studentMemberIds: string[],
      currentClassId: string | null | undefined,
    ): Record<number, string[]> => {
      const conflictTimes: Record<number, string[]> = {};

      if (!studentMemberIds || studentMemberIds.length === 0) {
        return conflictTimes;
      }

      // Filtere Schedules: nur andere Klassen UND nur primäre Stundenpläne
      const otherClassesSchedules = schedules.filter(schedule =>
        schedule.class_id !== currentClassId && schedule.is_primary === true
      );
      
      otherClassesSchedules.forEach((currentSchedule) => {
        const availableTimes = currentSchedule.available_times || {};
        
        Object.entries(availableTimes as Record<string, unknown>).forEach(([dayIndex, timeSlots]) => {
          const parsedDayIndex = parseInt(dayIndex);
          const dayName = completeDaysList[parsedDayIndex];
          if (!dayName) {
            console.warn(`[Table.js convertScheduleToConflictTimes] Invalid day index: ${dayIndex}`);
            return;
          }
          
          if (!conflictTimes[parsedDayIndex]) {
            conflictTimes[parsedDayIndex] = [];
          }
          
          if (Array.isArray(timeSlots)) {
            timeSlots.forEach((slot: unknown) => {
              if (Array.isArray(slot) && slot.length >= 2) {
                const time = slot[0];
                const slotMemberId = slot[1];
                
                // Nur hinzufügen, wenn die member_id des Slots zu unserem Studenten gehört
                if (studentMemberIds.includes(slotMemberId)) {
                  if (time && !conflictTimes[parsedDayIndex].includes(time)) {
                    conflictTimes[parsedDayIndex].push(time);
                  }
                }
              }
            });
          }
        });
      });
      
      debugHover("[Table.js convertScheduleToConflictTimes] conflictTimes", conflictTimes);
      return conflictTimes;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // *** Neue optimierte Funktion mit Cache-Lookup ***
    const loadStudentSchedules = useCallback(async (studentUserId: string | null | undefined) => {
      if (!studentUserId) {
        return { schedules: [] as StudentScheduleRow[], member_ids: [] as string[], classes_with_primary: [] as unknown[] };
      }
      
      // Prüfe Cache zuerst
      const cachedResult = getSchedulesForStudent(studentUserId);
      if (cachedResult) {
        debugHover(`[Table.js loadStudentSchedules] Cache hit for ${studentUserId}`);
        return cachedResult;
      }
      
      // Fallback: Lade individuell wenn nicht im Cache
      debugHover(`[Table.js loadStudentSchedules] Cache miss for ${studentUserId}`);
      return await loadSchedulesForStudent(studentUserId);
    }, [getSchedulesForStudent, loadSchedulesForStudent]);

    // Memoized Event Handlers using useCallback
    const handleMouseOver = useCallback((event: MouseEvent) => {
        const target = event.target as Element;
        const closestOverlay = target.closest("div." + studentOverlayStyles.studentOverlay);
        if (closestOverlay) {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            
            // Speichere Mouse-Position für später
            const mouseX = event.clientX || 0;
            const mouseY = event.clientY || 0;
            
            timerRef.current = setTimeout(() => {
                // Check studentsList inside timeout closure
                if (!studentsList) {
                    console.warn("studentsList is null/undefined inside handleMouseOver timeout");
                    return;
                }
                const currentStudent = studentsByMemberId.get(closestOverlay.id);
                if (!currentStudent) {
                  debugHover(`[Table.js handleMouseOver] No student found for ${closestOverlay.id}`);
                  return;
                }

                // Use timePreferences to determine which times to use
                let availableTimesToUse: any;
                const preference = timePreferences?.[closestOverlay.id];
                
                // Use the preference if set
                if (preference === 'available_times' && currentStudent.available_times) {
                  availableTimesToUse = currentStudent.available_times;
                } else if (preference === 'available_times_user_input' && currentStudent.available_times_user_input) {
                  availableTimesToUse = currentStudent.available_times_user_input;
                } 
                // Fallback to old logic if preference not set
                else if (currentStudent.available_times) {
                  availableTimesToUse = currentStudent.available_times;
                } else if (currentStudent.available_times_user_input) {
                  availableTimesToUse = currentStudent.available_times_user_input;
                } else if (currentStudent.free_times) {
                  availableTimesToUse = currentStudent.free_times;
                }

                if (!currentStudent || !availableTimesToUse) {
                     debugHover(`[Table.js handleMouseOver] No available times for ${closestOverlay.id}`);
                     return;
                }
                toggleHighlightCells(availableTimesToUse, true, globalStyles.highlight, "");
                if (currentStudent.favorite_times) {
                  toggleHighlightCells(currentStudent.favorite_times, true, globalStyles.favoriteHighlight, "");
                }

                // Lade und markiere Konflikt-Zeiten (nur für registrierte Studenten)
                if (currentStudent && currentStudent.user_id) {
                  loadStudentSchedules(currentStudent.user_id).then(result => {
                    const schedules: StudentScheduleRow[] = result.schedules || [];
                    const studentMemberIds: string[] = result.member_ids || [];
                    const classesWithPrimary = (result.classes_with_primary || []) as string[];

                    if (schedules.length > 0 && studentMemberIds.length > 0) {
                      const conflictTimes = convertScheduleToConflictTimes(schedules, studentMemberIds, classId);
                      toggleHighlightCells(conflictTimes, true, globalStyles.scheduleConflict, "");

                      // Zeige Tooltip
                      setTooltipState({
                        visible: true,
                        position: { x: mouseX, y: mouseY },
                        schedules: schedules,
                        studentMemberIds: studentMemberIds,
                        classesWithPrimary: classesWithPrimary
                      });
                    } else {
                      setTooltipState({ visible: false, position: null, schedules: [], studentMemberIds: [], classesWithPrimary: [] });
                      if (studentMemberIds.length === 0) {
                        console.warn(`[Table.js handleMouseOver] No member_ids received from API`);
                      }
                    }
                  }).catch((error: unknown) => {
                    console.error(`[Table.js handleMouseOver] Error in loadStudentSchedules promise:`, error);
                  });
                } else {
                  debugHover(`[Table.js handleMouseOver] Student ${closestOverlay.id} has no user_id`);
                }
            }, 300);
        }
    }, [studentsList, studentsByMemberId, toggleHighlightCells, timePreferences, loadStudentSchedules, convertScheduleToConflictTimes, classId, setTooltipState]);

    const handleMouseOut = useCallback((event: MouseEvent) => {
        // Verstecke Tooltip
        setTooltipState({ visible: false, position: null, schedules: [], studentMemberIds: [], classesWithPrimary: [] });
        
        const target = event.target as Element;
        const relatedTarget = event.relatedTarget as Node | null;
        const closestOverlay = target.closest("div." + studentOverlayStyles.studentOverlay);

        if (closestOverlay) {
            if (relatedTarget && closestOverlay.contains(relatedTarget)) {
                // Still inside the same overlay, do nothing
                return;
            }

            // Clear pending highlight timer
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }

            if (!studentsList) {
                console.warn("studentsList is null/undefined in handleMouseOut for member_id:", closestOverlay.id);
                return; // Prevent error
            }

            const currentStudent = studentsByMemberId.get(closestOverlay.id);
            if (!currentStudent) {
              debugHover(`[Table.js handleMouseOut] No student found for ${closestOverlay.id}`);
              return;
            }

            // Use timePreferences to determine which times to use
            let availableTimesToUse: any;
            const preference = timePreferences?.[closestOverlay.id];
            
            // Use the preference if set
            if (preference === 'available_times' && currentStudent.available_times) {
              availableTimesToUse = currentStudent.available_times;
            } else if (preference === 'available_times_user_input' && currentStudent.available_times_user_input) {
              availableTimesToUse = currentStudent.available_times_user_input;
            } 
            // Fallback to old logic if preference not set
            else if (currentStudent.available_times) {
              availableTimesToUse = currentStudent.available_times;
            } else if (currentStudent.available_times_user_input) {
              availableTimesToUse = currentStudent.available_times_user_input;
            } else if (currentStudent.free_times) {
              availableTimesToUse = currentStudent.free_times;
            }

            // Check if student and times exist
            if (!currentStudent || !availableTimesToUse) {
                debugHover(`[Table.js handleMouseOut] No available times for ${closestOverlay.id}`);
                return;
            }
            
            // Entferne auch Konflikt-Markierungen (nur für registrierte Studenten)
            if (currentStudent && currentStudent.user_id) {
              // *** Nutze Cache-Lookup statt lokalem State ***
              const cachedData = getSchedulesForStudent(currentStudent.user_id);
              const schedules = cachedData?.schedules || [];
              const studentMemberIds = cachedData?.member_ids || [];
              
              if (schedules.length > 0 && studentMemberIds.length > 0) {
                const conflictTimes = convertScheduleToConflictTimes(schedules, studentMemberIds, classId);
                toggleHighlightCells(conflictTimes, false, globalStyles.scheduleConflict);
              }
            } else {
              debugHover(`[Table.js handleMouseOut] Student ${closestOverlay.id} has no user_id`);
            }

            // Remove highlight
            toggleHighlightCells(availableTimesToUse, false, globalStyles.highlight, "");
            if (currentStudent.favorite_times) {
              toggleHighlightCells(currentStudent.favorite_times, false, globalStyles.favoriteHighlight, "");
            }
        }
    }, [studentsList, studentsByMemberId, toggleHighlightCells, timePreferences, getSchedulesForStudent, convertScheduleToConflictTimes, classId, setTooltipState]);

    // Assuming debounce creates a memoized function or handles updates internally.
    // If debounce causes issues, might need further refinement.
    const handleMouseMove = useCallback(debounce((e: MouseEvent) => { // eslint-disable-line react-hooks/exhaustive-deps
        elementBelowCursorRef.current = document.elementFromPoint(e.clientX, e.clientY);
        
        // Update Tooltip-Position wenn sichtbar
        setTooltipState(prev => {
          if (prev.visible) {
            return {
              ...prev,
              position: { x: e.clientX, y: e.clientY }
            };
          }
          return prev;
        });
    }, 50), [setTooltipState]); // setTooltipState is stable from useState

    // resetAllBackgrounds using useCallback
    const resetAllBackgrounds = useCallback(() => {
      // Use class selector for potentially better performance and robustness
      const hoveredCells = document.querySelectorAll("." + globalStyles.highlight);
      hoveredCells.forEach((cell) => {
        cell.classList.remove(globalStyles.highlight);
      });
    }, []); // No dependencies

    // useEffect for adding/removing listeners using memoized handlers
    useEffect(() => {
        const tableElement = tableRef.current;

        // Define window mouseout handler separately for clarity
        const handleWindowMouseOut = (event: MouseEvent) => {
            if (event.relatedTarget === null) {
                console.log("--- Window Mouse Out ---");
              resetAllBackgrounds();
            }
          };

        if (tableElement) {
            console.log("Adding event listeners");
          tableElement.addEventListener('mouseover', handleMouseOver);
          tableElement.addEventListener('mouseout', handleMouseOut);
            tableElement.addEventListener('mousemove', handleMouseMove, { passive: true });
            window.addEventListener('mouseout', handleWindowMouseOut);
        }
    
        // Cleanup function
        return () => {
            console.log("Removing event listeners");
            // Clear timer on cleanup
            if (timerRef.current) {
                clearTimeout(timerRef.current);
                timerRef.current = null;
            }
            // Remove listeners using the same memoized references
          if (tableElement) {
            tableElement.removeEventListener('mouseover', handleMouseOver);
            tableElement.removeEventListener('mouseout', handleMouseOut);
                // passive ist nur eine Add-Option; beim Remove zählt allein capture (default)
                tableElement.removeEventListener('mousemove', handleMouseMove);
          }
          window.removeEventListener('mouseout', handleWindowMouseOut);
        };
    // Depend only on the memoized handlers and reset function
    }, [handleMouseOver, handleMouseOut, handleMouseMove, resetAllBackgrounds]);

  

    const isAvailable = useCallback((memberId: string, day: number | string, time: string): boolean => {
        if (!studentsList || !studentsByMemberId.get(memberId)) return false;

        const thisStudent = studentsByMemberId.get(memberId)!;

        const studentDurationMins = thisStudent.duration;
        if (!studentDurationMins) return false;

        const studentDurationSlots = Number(studentDurationMins) / 15;
        // dayTimeToSlot(0, time) liefert direkt den Slot-Index innerhalb eines
        // Tages (0..95) und normalisiert dabei führende Nullen ("08.00"=="8.00"),
        // statt manuell zu stripen und per indexOf zu suchen.
        const startTimeId = dayTimeToSlot(0, time);

        if (startTimeId === null) return false;

        for (let i = 0; i < studentDurationSlots; i++) {
            const currentTimeIndex = startTimeId + i;
            if (currentTimeIndex >= completeTimesList.length) return false;
            const currentTime = completeTimesList[currentTimeIndex];
            if (!isAvailableInternal(memberId, day, currentTime)) {
                return false;
            }
        }
        return true;
    }, [studentsList, studentsByMemberId, isAvailableInternal]);

    // handleDropConfirmed anpassen, um setErgebnis zu verwenden
    const handleDropConfirmed = useCallback((name: string, durationSlots: number, day: string | number, time: string, memberId: string, userId?: string | null) => {
      const dayString = completeDaysList[day as number];
      const startTimeId = completeTimesList.indexOf(time);

      console.log("[Table.js handleDropConfirmed]", name, "memberId:", memberId, "durationSlots:", durationSlots, "day:", day, "time:", time, "memberId:", memberId, "userId:", userId);

      if (startTimeId === -1) {
          console.error("[Table.js handleDropConfirmed] Ungültige Startzeit:", time);
          return;
      }

      const endTimeId = startTimeId + durationSlots;

      const newSchedule: DisplaySchedule = JSON.parse(JSON.stringify(schedule || {}));

      Object.keys(newSchedule).forEach(dKey => {
          if (newSchedule[dKey]) {
              Object.keys(newSchedule[dKey]).forEach(tKey => {
                  if (newSchedule[dKey][tKey] === memberId) {
                      newSchedule[dKey][tKey] = "";
                  }
              });
              if (Object.values(newSchedule[dKey]).every(val => val === "")) {
                  delete newSchedule[dKey];
              }
          }
      });

      if (!newSchedule[dayString]) newSchedule[dayString] = {};
      for (let i = 0; i < durationSlots; i++) {
          const currentTimeIndex = startTimeId + i;
          if (currentTimeIndex < completeTimesList.length) {
               const currentSlotTime = completeTimesList[currentTimeIndex];
               if (!memberId || memberId === '') continue
               newSchedule[dayString][currentSlotTime] = memberId;
          } else {
              console.warn(`[Table.js handleDropConfirmed] Zeitindex ${currentTimeIndex} außerhalb von completeTimesList beim Setzen für ${name}`);
          }
      }
      console.log("[Table.js handleDropConfirmed] New schedule:", newSchedule);

      const updatedStudentEntry = {
          name: name,
          set_day: parseInt(String(day)),
          set_time_from_to: [startTimeId, endTimeId],
          is_set: true,
          is_not_assigned: false,
          memberId: memberId,
          ...(userId !== undefined && { user_id: userId })
      };
      console.log("[Table.js handleDropConfirmed] Calculated updated student entry:", updatedStudentEntry);

      if (typeof onScheduleUpdate === 'function') {
          onScheduleUpdate(newSchedule, updatedStudentEntry);
      } else {
          console.error("onScheduleUpdate is not a function!");
      }

    }, [schedule, onScheduleUpdate]); // eslint-disable-line react-hooks/exhaustive-deps

    // onDrop Callback anpassen
    const onDrop = useCallback((itemInfo: StudentDragItem, targetDay: string | number, targetTime: string) => {
      const memberId = itemInfo.member_id;
      const userId = itemInfo.user_id;
      const { name, duration } = itemInfo;
      console.log("itemInfo:")
      console.log(itemInfo)
      console.log("[Table.js onDrop] Dropped:", name, " (" + memberId + ", )duration:",  duration, "on day:", targetDay, "at time:", targetTime);

      const targetStartTimeId = completeTimesList.indexOf(targetTime);
      if (targetStartTimeId === -1) {
          console.error("[Table.js onDrop] Ungültige Zielzeit:", targetTime);
          return;
      }

      let availableAtTarget = isAvailable(memberId, targetDay, targetTime);

      if (!availableAtTarget) {
          console.log(`[Table.js onDrop] ${name} ist nicht verfügbar an Tag ${targetDay} um ${targetTime}. Öffne Modal.`);
          setConfirmModal({
              isOpen: true,
              studentName: name,
              pendingDrop: { name, durationSlots: duration, day: targetDay, time: targetTime, memberId, userId }
          });
          return;
      }

      console.log(`[Table.js onDrop] ${name} ist verfügbar. Rufe handleDropConfirmed.`);
      handleDropConfirmed(name, duration, targetDay, targetTime, memberId, userId);
  }, [isAvailable, handleDropConfirmed, setConfirmModal]);

    // onDelete anpassen, um setErgebnis zu verwenden
    const onDelete = useCallback((memberId: string) => {
        console.log("[Table.js onDelete] Preparing delete for:", memberId);

        // CRITICAL: Entferne Hover-Effekt bevor Student gelöscht wird
        // Clear pending highlight timer
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        // Finde den Student und entferne seinen Hover-Effekt
        if (studentsList) {
            const currentStudent = studentsByMemberId.get(memberId);
            if (currentStudent) {
                // Use timePreferences to determine which times to use
                let timesToUse: any;
                const preference = timePreferences?.[memberId];
                
                // Use the preference if set
                if (preference === 'available_times' && currentStudent.available_times) {
                  timesToUse = currentStudent.available_times;
                } else if (preference === 'available_times_user_input' && currentStudent.available_times_user_input) {
                  timesToUse = currentStudent.available_times_user_input;
                } 
                // Fallback to old logic if preference not set
                else if (currentStudent.available_times) {
                  timesToUse = currentStudent.available_times;
                } else if (currentStudent.available_times_user_input) {
                  timesToUse = currentStudent.available_times_user_input;
                } else if (currentStudent.free_times) {
                  timesToUse = currentStudent.free_times;
                }
                
                if (timesToUse) {
                    // Entferne Hover-Highlighting
                    toggleHighlightCells(timesToUse, false, globalStyles.highlight, "");
                }
            }
        }

        const newSchedule: DisplaySchedule = JSON.parse(JSON.stringify(schedule || {}));
        let found = false;
        
        // Suche nach dem Student in allen Tagen und Zeit-Slots
        for (const dKey of Object.keys(newSchedule)) {
            if (newSchedule[dKey]) {
                let dayWasModified = false;
                
                // Suche in den Zeit-Slots dieses Tages
                for (const tKey of Object.keys(newSchedule[dKey])) {
                    if (newSchedule[dKey][tKey] === memberId) {
                        delete newSchedule[dKey][tKey]
                        found = true;
                        dayWasModified = true;
                        
                        // KEIN break hier - Student könnte an mehreren Zeiten am selben Tag sein
                    }
                }
                
                // Cleanup: Lösche Tag nur wenn er modifiziert wurde UND alle Slots leer sind
                if (dayWasModified && Object.values(newSchedule[dKey]).every(val => val === "")) {
                    delete newSchedule[dKey];
                }
            }
        }
        
        if (found) console.log("[Table.js onDelete] Calculated new schedule after delete:", newSchedule);
        else console.warn(`[Table.js onDelete] Student ${memberId} nicht im Schedule gefunden zum Löschen.`);

        const updatedStudentEntry = {
            memberId: memberId,
            set_day: null,
            set_time_from_to: [],
            is_set: false,
        };
        console.log("[Table.js onDelete] Calculated updated student entry (deleted):", updatedStudentEntry);

        if (typeof onScheduleUpdate === 'function') {
            onScheduleUpdate(newSchedule, updatedStudentEntry);
        } else {
            console.error("onScheduleUpdate is not a function!");
        }

    }, [schedule, onScheduleUpdate, studentsList, studentsByMemberId, toggleHighlightCells, timePreferences]);

    return (
        <>
            <div
                className={styles.tableContainer}
            >
                <div className={`${styles.divTable} ${styles.compact}`} ref={tableRef}>
                    <div className={styles.divTableHead}>
                        <div className={styles.divTableRow}>
                            <div className={`${styles.timeColumnHeader} ${styles.timeColumn} ${styles.divTableData}`}></div>
                            <div className={styles.divTableData} data-daycolumn="0" >{isMobile ? "Mo" : "Montag"}</div>
                            <div className={styles.divTableData} data-daycolumn="1" >{isMobile ? "Di" : "Dienstag"}</div>
                            <div className={styles.divTableData} data-daycolumn="2" >{isMobile ? "Mi" : "Mittwoch"}</div>
                            <div className={styles.divTableData} data-daycolumn="3" >{isMobile ? "Do" : "Donnerstag"}</div>
                            <div className={styles.divTableData} data-daycolumn="4" >{isMobile ? "Fr" : "Freitag"}</div>
                            <div className={styles.divTableData} data-daycolumn="5" >{isMobile ? "Sa" : "Samstag"}</div>
                            <div className={styles.divTableData} data-daycolumn="6" >{isMobile ? "So" : "Sonntag"}</div>

                        </div>
                    </div>
                    <div className={styles.divTBody}>
                        {timesList && timesList.length > 0 ? timesList.map((time, timeindex) => {
                            const minutes = String(time.slice(-2));
                            let rowType = '';
                            switch (minutes) {
                              case '00':
                                rowType = 'fullHour';
                                break;
                              case '30':
                                rowType = 'halfPast';
                                break;
                              case '15':
                                rowType = 'quarterPast';
                                break;
                              case '45':
                                rowType = 'quarterTo';
                                break;
                              default:
                                rowType = 'error';
                                break;
                            }

                            return (
                                <div className={styles.divTableRow} key={time} data-time={time} data-rowtype={rowType}>
                                    {minutes === '00' ? (
                                        <div className={styles.timeColumn + " " + styles.divTableData}>{time}</div>
                                    ) : (
                                        <div className={styles.timeColumn}></div>
                                    )}

                                    {completeDaysList.map((dayName, index) => {
                                        const cellKey = `${index}_${time}`;

                                        return (
                                            <TableCell
                                                key={cellKey}
                                                day={index}
                                                time={time}
                                                id={cellKey}
                                                onDrop={onDrop}
                                                onDelete={onDelete}
                                                onLockToggle={onLockToggle}
                                                highlightedStudent={highlightedStudent}
                                                studentOverlays={overlaysByCell.get(`${index}_${time}`) || []}
                                            />
                                        );
                                    })}
                                </div>
                            );
                        }) : <div className={styles.noTimeMessage}>Bitte wählen Sie einen Zeitbereich in der Seitenleiste aus</div>}
                    </div>
                </div>
            </div>

            <ConfirmModal 
                isOpen={confirmModal.isOpen}
                title="Verfügbarkeit überschreiben?"
                message={`${confirmModal.studentName} ist zu dieser Zeit nicht verfügbar. Trotzdem eintragen?`}
                onClose={() => setConfirmModal({ isOpen: false, studentName: '', pendingDrop: null })}
                onConfirm={() => {
                    if (confirmModal.pendingDrop) {
                        const { name, durationSlots, day, time, memberId, userId } = confirmModal.pendingDrop;
                        handleDropConfirmed(name, durationSlots, day, time, memberId, userId);
                    }
                    setConfirmModal({ isOpen: false, studentName: '', pendingDrop: null });
                }}
            />
            {tooltipState.visible && tooltipState.position && classId && (
              <ScheduleTooltip
                schedules={tooltipState.schedules}
                studentMemberIds={tooltipState.studentMemberIds}
                position={tooltipState.position}
                currentClassId={classId}
                classesWithPrimary={tooltipState.classesWithPrimary}
              />
            )}
        </>
    );
}
      
